import { Platform } from "react-native";
import { PlatformStackScreenOptions } from "./createPlatformStackNavigator";

// Extra options for the form sheets whose body is a native PDF view. That view fills its parent
// with `flex: 1`, so an ancestor must resolve a height for it.
//
// On iOS a form sheet positions the screen content with `{position: 'absolute', top/start/end}` and
// deliberately NO `bottom` (react-native-screens `getPositioningStyle` -> `absoluteWithNoBottom`,
// so Yoga cannot resize the sheet mid-animation). The content box therefore never resolves a
// height, `flex: 1` measures 0, and the PDF renders blank — no loader, no error (BAS-2889).
//
// Re-adding that single inset gives the content the sheet-sized viewport back: `contentStyle` is
// merged AFTER the positioning style (`DebugContainer` renders `style={[style, contentStyle]}`) and
// on an iOS form sheet only `backgroundColor` is lifted out of it (`extractScreenStyles`), so
// `bottom` reaches the wrapper. The viewport stays sheet-sized, never document-sized, so the PDF
// keeps its own scroll view for pinch-zoom + pan.
//
// Deliberately NOT `featureFlags.experiment.synchronousScreenUpdatesEnabled`, which fixes the same
// layout but also switches EVERY screen's shadow-state update to `unstable_Immediate` app-wide.
// Breaks again on any screen that sets `sheetAllowedDetents: 'fitToContents'` (same code path).
//
// Lives in its own module so the Storybook story that exercises the sheet can reuse it without
// importing the whole navigator.
export const PDF_SHEET_CONTENT: PlatformStackScreenOptions =
    Platform.OS === "ios" ? { contentStyle: { bottom: 0 } } : {};

// Which platforms may present a PDF screen as a form sheet.
//
// Android form sheets are Material bottom sheets driven by `BottomSheetBehavior`. That behavior
// only yields a vertical drag to a child that advertises itself as a nested scrolling child (a
// RecyclerView, a ScrollView...). The native PDF view is neither: `PdfView extends PDFView` reads
// its gestures from a plain `OnTouchListener` and never calls `requestDisallowInterceptTouchEvent`.
// The sheet therefore intercepts the drag before the document ever sees it, and the PDF only scrolls
// on the gestures the sheet happens to let through (BAS-2890).
//
// The PDF owns its scroll on every platform (see utils/pdf-scroll.ts), so there is no scroll to hand
// back to the sheet: Android presents these screens as full modals instead. iOS page sheets have no
// such conflict — UIKit forwards the drag to the topmost scroll view, which is PDFKit's own.
export const PDF_SHEET_PLATFORMS: { [k in typeof Platform.OS]?: boolean } = {
    ios: true,
    android: false,
};
